import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

const STORAGE_KEY = 'bmb_newsletter_last_shown';
const COOLDOWN_DAYS = 5;
const AUTO_OPEN_DELAY = 3000;

function shouldShowModal() {
  try {
    const lastShown = localStorage.getItem(STORAGE_KEY);
    if (!lastShown) return true;
    const daysSince = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60 * 24);
    return daysSince >= COOLDOWN_DAYS;
  } catch {
    return true;
  }
}

function markAsShown() {
  try {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
  } catch {}
}

const NewsletterWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [privacy, setPrivacy] = useState(false);
  const [tracking, setTracking] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const openModal = useCallback(() => {
    setIsOpen(true);
    markAsShown();
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (shouldShowModal()) {
      const timer = setTimeout(openModal, AUTO_OPEN_DELAY);
      return () => clearTimeout(timer);
    }
  }, [openModal]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!privacy) {
      setStatus({ type: 'error', message: 'Please accept the Privacy Policy to continue.' });
      return;
    }

    setSubmitting(true);
    setStatus(null);

    try {
      const res = await fetch('https://weljffoiqzulxdwrhvop.supabase.co/functions/v1/newsletter-widget', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          firstName,
          lastName,
          consentTracking: tracking,
          source: window.location.hostname,
        }),
      });
      const data = await res.json();

      if (res.ok) {
        setStatus({ type: 'success', message: 'Thanks for subscribing! Check your inbox soon.' });
        setFirstName('');
        setLastName('');
        setEmail('');
        setPrivacy(false);
        setTracking(false);
        setTimeout(closeModal, 2000);
      } else {
        setStatus({ type: 'error', message: data.error || 'Something went wrong' });
      }
    } catch {
      setStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    }

    setSubmitting(false);
  };

  return createPortal(
    <>
      {/* FAB Button */}
      <button
        onClick={openModal}
        className="bmb-fab"
        aria-label="Open newsletter signup"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'hsl(240 10% 4%)' }}>
          <rect width="20" height="16" x="2" y="4" rx="2" />
          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
        </svg>
      </button>

      {/* Modal Overlay */}
      {isOpen && (
        <div
          className="bmb-overlay bmb-open"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="bmb-modal">
            <button className="bmb-close" onClick={closeModal} aria-label="Close newsletter">
              ×
            </button>
            <div className="bmb-widget">
              <div className="bmb-widget-header">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F7931A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="20" height="16" x="2" y="4" rx="2" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
                <h3 className="bmb-widget-title">Subscribe to Our Newsletter</h3>
              </div>
              <p className="bmb-widget-subtitle">Get the latest updates and insights delivered to your inbox</p>

              <form onSubmit={handleSubmit}>
                <div className="bmb-row">
                  <div>
                    <label htmlFor="bmb-firstname">First Name</label>
                    <input
                      type="text"
                      id="bmb-firstname"
                      placeholder="John"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="bmb-lastname">Last Name</label>
                    <input
                      type="text"
                      id="bmb-lastname"
                      placeholder="Doe"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="bmb-email">Email Address <span className="bmb-required">*</span></label>
                  <input
                    type="email"
                    id="bmb-email"
                    placeholder="john@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="bmb-checkbox-group">
                  <div className="bmb-checkbox-item">
                    <input
                      type="checkbox"
                      id="bmb-privacy"
                      checked={privacy}
                      onChange={(e) => setPrivacy(e.target.checked)}
                      required
                    />
                    <label htmlFor="bmb-privacy">
                      I accept the <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and agree to receive newsletters. <span className="bmb-required">*</span>
                    </label>
                  </div>
                  <div className="bmb-checkbox-item bmb-optional">
                    <input
                      type="checkbox"
                      id="bmb-tracking"
                      checked={tracking}
                      onChange={(e) => setTracking(e.target.checked)}
                    />
                    <label htmlFor="bmb-tracking">
                      I consent to email tracking (opens and clicks) to help improve content relevance. This is optional.
                    </label>
                  </div>
                </div>

                <button type="submit" className="bmb-submit-btn" disabled={submitting}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="20" height="16" x="2" y="4" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                  {submitting ? 'Subscribing...' : 'Subscribe'}
                </button>
              </form>

              {status && (
                <div className={`bmb-message ${status.type === 'success' ? 'bmb-success' : 'bmb-error'}`}>
                  {status.message}
                </div>
              )}

              <p className="bmb-footer">
                Your data is processed according to GDPR. Manage preferences, export or delete your data at any time.
              </p>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default NewsletterWidget;
